import { useState } from "react";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import { useEscapeKey } from "../../hooks/useEscapeKey";
import { useThemeContext } from "../../theme/ThemeContext";
import { overlayBackdrop } from "../../theme/styles";
import { RADIUS } from "../../theme/tokens";
import { Button, AirlineLogo, SA_AIRLINES, LHG_CARRIERS } from "../shared";
import { dbUpdateAirlinePassword } from "../../db";
import { TOAST_DURATION_MS } from "../../utils/constants";

export function AdminPasswordModal({ onClose }) {
  const { SA, GLASS, tokens, TYPOGRAPHY } = useThemeContext();
  useLockBodyScroll();
  useEscapeKey(onClose);
  const [inputs, setInputs]         = useState({});
  const [saving, setSaving]         = useState({});
  const [saved, setSaved]           = useState({});

  const allAirlines = [
    { code: "LHG", name: "Lufthansa Group (LH, OS, SN, 4Y, LX, WK)" },
    ...SA_AIRLINES.filter(a => a.code !== "UA" && a.code !== "LHG" && !LHG_CARRIERS.includes(a.code)),
  ];

  const handleSave = async (code) => {
    const val = (inputs[code] || "").trim();
    if (!val) return;
    setSaving(s => ({ ...s, [code]: true }));
    await dbUpdateAirlinePassword(code, val);
    setInputs(i => ({ ...i, [code]: "" }));
    setSaved(s => ({ ...s, [code]: true }));
    setTimeout(() => setSaved(s => ({ ...s, [code]: false })), TOAST_DURATION_MS);
    setSaving(s => ({ ...s, [code]: false }));
  };

  return (
    <div onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }} style={{ ...overlayBackdrop, zIndex: 200,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16, overflowY: "auto" }}>
      <div onClick={e => e.stopPropagation()} style={{ ...GLASS.modal,
        width: "100%", maxWidth: 440,
        borderRadius: 16, maxHeight: "85vh",
        display: "flex", flexDirection: "column" }}>

        {/* Header */}
        <div style={{ padding: "18px 20px 14px", borderBottom: `1px solid ${SA.borderGray}`,
          display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: TYPOGRAPHY.label, color: SA.white, letterSpacing: "0.18em", textTransform: "uppercase" }}>
            Manage Passwords
          </div>
          <Button variant="icon" onClick={onClose}
            style={{ background: SA.medGray, color: SA.silver, width: 28, height: 28, fontSize: 16 }}>×</Button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px" }}>
          <div style={{ fontSize: TYPOGRAPHY.tiny, color: SA.silver, letterSpacing: "0.06em", marginBottom: 20, lineHeight: 1.6 }}>
            Set or update passwords for each airline. Partners only see their own connections.
            Leave blank to keep existing password.
          </div>

          {allAirlines.map(a => (
            <div key={a.code} style={{ display: "flex", alignItems: "center", gap: 8,
              padding: "10px 0", borderBottom: `1px solid ${SA.borderGray}` }}>
              <div style={{ width: 36, flexShrink: 0 }}>
                <AirlineLogo iata={a.code} size={28} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: TYPOGRAPHY.tiny, color: SA.borderGray, letterSpacing: "0.1em", marginBottom: 4 }}>
                  {a.code}
                </div>
                <input
                  type="password"
                  placeholder="New password..."
                  value={inputs[a.code] || ""}
                  onChange={e => setInputs(i => ({ ...i, [a.code]: e.target.value }))}
                  onKeyDown={e => e.key === "Enter" && handleSave(a.code)}
                  style={{ width: "100%", background: SA.darkGray, border: `1px solid ${SA.borderGray}`,
                    color: SA.white, padding: "7px 10px", fontSize: TYPOGRAPHY.body, borderRadius: 2,
                    fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", outline: "none" }}
                />
              </div>
              <Button onClick={() => handleSave(a.code)}
                disabled={!inputs[a.code]?.trim() || saving[a.code]}
                style={{ padding: "7px 12px", background: saved[a.code] ? tokens.successSurface : SA.medGray,
                  border: `1px solid ${saved[a.code] ? tokens.successBorder : SA.borderGray}`,
                  color: saved[a.code] ? tokens.monoGreen : SA.white, fontSize: TYPOGRAPHY.tiny,
                  letterSpacing: "0.1em", textTransform: "uppercase",
                  fontFamily: "'Helvetica Neue',Arial,sans-serif", borderRadius: 2, flexShrink: 0 }}>
                {saved[a.code] ? "v" : saving[a.code] ? "..." : "Set"}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
